import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  ResourceCards,
  Step1NameAgent,
  Step2SetupCli,
  Step3CreateBot,
  Step4Deploy,
  StepSidebar,
  STEPS,
} from '~/features/bots/components/adk-onboarding'
import { useUpdateBot } from '~/hooks/bots/useUpdateBot'
import { trackEvent } from '~/providers'
import { getQueryKey, useSuspenseQuery } from '~/services'
import { resolveKey } from '~/services/preferences/preferences'
import { setWorkspacePreference } from '~/services/preferences/workspacePreferences'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/new-adk-bot-setup')({
  component: RouteComponent,
})

function RouteComponent() {
  const { workspaceId, botId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: bot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId })
  const { data: workspace } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId })
  const [currentStep, setCurrentStep] = useState(1)
  const [agentName, setAgentName] = useState('')
  const updateBot = useUpdateBot()

  // Track onboarding started
  useEffect(() => {
    trackEvent({ type: 'adk_onboarding_started', botId, workspaceId })
  }, [botId, workspaceId])

  const trackStepCompleted = (step: number) => {
    const stepInfo = STEPS.find((s) => s.number === step)
    trackEvent({
      type: 'adk_onboarding_step_completed',
      botId,
      workspaceId,
      step,
      stepName: stepInfo?.title ?? `Step ${step}`,
    })
  }

  const handleComplete = async () => {
    trackStepCompleted(4)
    trackEvent({ type: 'adk_onboarding_completed', botId, workspaceId })
    // Set preference to false
    await setWorkspacePreference({ path: 'isAdkOnboarding/$botId', params: { botId }, value: false }, workspaceId)

    // Update the query cache directly so the parent layout doesn't redirect back
    const preferenceQueryKey = getQueryKey('workspaces_/$workspaceId_/preferences/$path', {
      path: resolveKey({ path: 'isAdkOnboarding/$botId', params: { botId } }),
      workspaceId,
    })
    queryClient.setQueryData(preferenceQueryKey, false)

    // Navigate to overview
    await navigate({ to: '/workspaces/$workspaceId/bots/$botId/overview', params: { workspaceId, botId } })
  }

  const handleStep1Next = () => {
    updateBot.mutate({
      workspaceId,
      id: botId,
      name: agentName,
      onSuccess: () => {
        trackStepCompleted(1)
        setCurrentStep(2)
      },
    })
  }

  const handleStep2Next = () => {
    trackStepCompleted(2)
    setCurrentStep(3)
  }

  const handleSkipToStep3 = () => {
    trackStepCompleted(2)
    setCurrentStep(3)
  }

  const handleStep3Next = () => {
    trackStepCompleted(3)
    setCurrentStep(4)
  }

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 lg:px-20 lg:py-12">
      <div className="flex gap-20">
        {/* Main Content */}
        <div className="flex max-w-[850px] min-w-0 flex-1 flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase">Step {currentStep} of 4</p>

          {currentStep === 1 && (
            <Step1NameAgent
              agentName={agentName}
              onAgentNameChange={setAgentName}
              onNext={handleStep1Next}
              isPending={updateBot.isPending}
            />
          )}

          {currentStep === 2 && (
            <Step2SetupCli
              workspaceId={workspaceId}
              botId={botId}
              onNext={handleStep2Next}
              onSkip={handleSkipToStep3}
            />
          )}

          {currentStep === 3 && (
            <Step3CreateBot
              botName={agentName}
              workspaceName={workspace.name}
              workspaceId={workspaceId}
              botId={botId}
              onNext={handleStep3Next}
            />
          )}

          {currentStep === 4 && <Step4Deploy workspaceId={workspaceId} botId={botId} onComplete={handleComplete} />}

          {/* Resource cards - shown on mobile only */}
          <div className="mt-8 lg:hidden">
            <ResourceCards workspaceId={workspaceId} botId={botId} />
          </div>
        </div>

        {/* Sidebar - hidden on mobile */}
        <StepSidebar currentStep={currentStep} workspaceId={workspaceId} botId={botId} />
      </div>
    </div>
  )
}
